import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AnalyticsStore, QueryRoute, RouteStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { Layer } from 'bernie-view';

import { useLocalization } from '@shared-ui/localization-context';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';

import { GalleryViewModel, ImageViewModel } from 'src/common/models';

import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkTertiaryButton } from 'uitk-react-button';

import { GalleryDialog } from 'components/infosite/property-details/gallery-dialog';
import { GalleryThumbnailDialog } from 'components/infosite/property-details/gallery-thumbnail-dialog';

const FlatButtonTracked = ClickTracker(UitkTertiaryButton);

export interface GalleryTriggerProps {
  analytics?: AnalyticsStore;
  currentHotel?: CurrentHotelStore;
  routeStore?: RouteStore;
  className?: string;
  displayImageCount?: boolean;
  trackingRfrr?: string;
  onIndexChange?: (index) => void;
  currentIndex?: number;
  gallery?: GalleryViewModel;
  images: ImageViewModel[];
  id?: any;
  triggerId?: string;
  galleryTitle?: string;
  showThumbnail?: boolean;
  infositeGallery?: boolean;
}

export interface GalleryTriggerState {
  currentCategoryImageIndex: number;
  initialized: boolean;
}

@inject('analytics', 'currentHotel', 'routeStore')
@observer
export class GalleryTrigger extends React.Component<GalleryTriggerProps, GalleryTriggerState> {
  private galleryTriggerRef;

  constructor(props) {
    super(props);

    this.state = {
      currentCategoryImageIndex: 0,
      initialized: false
    };

    this.galleryDialogCloseCallback = this.galleryDialogCloseCallback.bind(this);
    this.galleryThumbnailDialogCloseCallback = this.galleryThumbnailDialogCloseCallback.bind(this);
    this.onCategoryImageIndexChanged = this.onCategoryImageIndexChanged.bind(this);
  }

  public static defaultProps: Partial<GalleryTriggerProps> = {
    triggerId: 'infosite-gallery'
  };

  public componentDidMount() {
    this.setState({ initialized: true });
  }

  private onCategoryImageIndexChanged(index: number) {
    this.setState({ currentCategoryImageIndex: index });
  }

  public render() {
    if (!this.state.initialized) {
      return null;
    }

    const {
      className,
      currentHotel,
      currentIndex,
      displayImageCount,
      id,
      images,
      galleryTitle,
      gallery,
      onIndexChange,
      routeStore,
      trackingRfrr,
      triggerId,
      showThumbnail,
      infositeGallery
    } = this.props;
    const { currentCategoryImageIndex } = this.state;

    let selectedImages;
    let selectedImageIndex;
    if (images) {
      selectedImages = images;
      selectedImageIndex = currentIndex;
    } else if (currentHotel.selectedGalleryCategory) {
      const selectedCategory = currentHotel.selectedGalleryCategory;
      selectedImages = selectedCategory.images;
      selectedImageIndex =
        selectedCategory.id !== CurrentHotelStore.DEFAULTS.selectedGalleryCategoryId ? currentCategoryImageIndex : currentIndex;
    } else if (gallery.images) {
      selectedImages = gallery.images;
      selectedImageIndex = currentIndex;
    }

    const imagesLength = selectedImages ? selectedImages.length : 0;
    if (imagesLength === 0) {
      return null;
    }

    const param = { pwaDialog: `photoGallery-${id}` };
    const query = routeStore.mergeQueryString(routeStore.search, param);
    const paramThumbnail = { pwaDialog: `thumbnailPhotoGallery-${id}` };
    const queryThumbnail = routeStore.mergeQueryString(routeStore.search, paramThumbnail);
    const galleryTriggerRef = galleryTrigger => {
      this.galleryTriggerRef = galleryTrigger;
    };

    const galleryTriggerButtonProps = {
      displayImageCountMessage: (displayImageCount && `${imagesLength - 1}+ `) || '',
      galleryTriggerRef,
      id,
      infositeGallery,
      linkName: 'Open Room Gallery dialog',
      pathname: routeStore.pathname,
      query: showThumbnail ? queryThumbnail : query,
      trackingRfrr
    };

    return (
      <div className={`gallery-trigger ${className || ''}`}>
        <GalleryTriggerButton {...galleryTriggerButtonProps} />
        <QueryRoute query={param}>
          <Layer id={triggerId} onDismiss={this.galleryDialogCloseCallback}>
            <GalleryDialog
              currentIndex={selectedImageIndex}
              galleryDialogCloseCallback={this.galleryDialogCloseCallback}
              galleryName="Dialog"
              images={selectedImages}
              onIndexChange={onIndexChange}
              trackingRfrr={trackingRfrr}
            />
          </Layer>
        </QueryRoute>
        <QueryRoute query={paramThumbnail}>
          <Layer id={`thumbnailGallery${triggerId}`} onDismiss={this.galleryThumbnailDialogCloseCallback}>
            <GalleryThumbnailDialog
              galleryDialogCloseCallback={this.galleryThumbnailDialogCloseCallback}
              galleryName="ThumnailDialog"
              gallery={gallery}
              onIndexChange={onIndexChange}
              onCategoryImageIndexChanged={this.onCategoryImageIndexChanged}
              trackingRfrr={trackingRfrr}
              galleryTitle={galleryTitle}
              id={id}
            />
          </Layer>
        </QueryRoute>
      </div>
    );
  }

  private galleryThumbnailDialogCloseCallback() {
    const { analytics, currentHotel, trackingRfrr } = this.props;
    analytics.trackEvent(`${trackingRfrr}.Close`, 'Photo gallery dialog has closed', analytics.omnitureData);
    currentHotel.setSelectedGalleryCategoryId(CurrentHotelStore.DEFAULTS.selectedGalleryCategoryId);
    this.onCategoryImageIndexChanged(currentHotel.photoGalleryIndex);
    this.galleryDialogCloseCallback();
  }

  private galleryDialogCloseCallback() {
    const routeStore = this.props.routeStore;

    if (routeStore && typeof routeStore.goBack === 'function') {
      routeStore.goBack();
    }

    if (this.galleryTriggerRef && typeof this.galleryTriggerRef.focus === 'function') {
      this.galleryTriggerRef.focus();
    }
  }
}

export interface GalleryTriggerButtonProps {
  clickHandler?: () => void;
  displayImageCountMessage?: string;
  galleryTriggerRef: any;
  id?: string;
  infositeGallery?: boolean;
  linkName?: string;
  pathname?: string;
  query?: string;
  trackingRfrr?: string;
}

export const GalleryTriggerButton = ({
  clickHandler,
  displayImageCountMessage,
  galleryTriggerRef,
  id,
  infositeGallery,
  linkName,
  pathname,
  query,
  trackingRfrr
}: GalleryTriggerButtonProps) => (
  <FlatButtonTracked
    innerRef={galleryTriggerRef}
    tag={Link}
    to={{ pathname, search: query }}
    className="gallery-trigger__btn uitk-type-left"
    linkName={linkName}
    padding="compact"
    rfrr={trackingRfrr}
    onClick={clickHandler}
  >
    <span className="uitk-type-300 uitk-type-medium pwa-theme--color-white gallery-trigger__btn-label">
      {displayImageCountMessage}
      <UitkIcon
        name="photo_library"
        size={UitkIconSize.SMALL}
        title={useLocalization().formatText('hotels.infosite.gallery.viewAll.title')}
        id={`gallery-trigger__${id}-icon`}
      />
      {infositeGallery && (
        <AbacusExperiment name="PWA_PDP_Gallery_Trigger_Text_Throttle">
          <AbacusControl />
          <AbacusVariant bucket={1}>
            <> {useLocalization().formatText('hotels.infosite.gallery.viewAll.label')}</>
          </AbacusVariant>
        </AbacusExperiment>
      )}
    </span>
  </FlatButtonTracked>
);

GalleryTriggerButton.displayName = 'GalleryTriggerButton';
